import React, { useEffect, useMemo, useState } from 'react'
import { Modal, ModalBody, Spinner } from 'reactstrap'
import { APIClient } from 'helpers/api_helper'
// 🔴 Yol SABİTTEN gelir, elle yazılmaz: mount yolu `/api/v1/photoBooths`
// (camelCase). Elle `/photo-booths` yazınca 404 alınıyordu — deploy öncesi
// canlı uçla doğrulanarak yakalandı.
import { BOOTH_RECORD_PATH } from 'helpers/url_helper'

/**
 * KABİN BAKIM MODU (müşteri talebi 2026-08-24).
 *
 * "Her kabinin yanında arıza butonu olsun. Basınca 'şu an bakımdayız' deyip
 *  hizmetleri deaktif etsin — hatta bakıma alınacak hizmeti seçtirelim."
 *
 * 🔴 NEDEN HİZMETİ BURADAN KAPATMIYORUZ: `ActiveAction.isActive` GLOBAL bir
 * alandır (tüm kabinler aynı belgeyi paylaşır). Oradan kapatmak Atlaspark
 * için basılan düğmenin Venezia'yı da kapatması demekti. Bakım kabin
 * belgesinde tutulur: `PUT /photo-booths/:id/maintenance`.
 *
 * Boş liste kaydetmek = bakımdan çıkarmak (ayrı bir "kapat" ucu yok).
 */

export interface IBoothService {
  _id: string
  label?: string
  name?: string
}

interface IProps {
  show: boolean
  boothId: string | null
  boothName: string
  /** Kabine ATANMIŞ hizmetler (populate edilmiş activeActions). */
  services: IBoothService[]
  /** Hâlihazırda bakımda olan hizmet kimlikleri. */
  initialSelected: string[]
  onClose: () => void
  onDone: (msg: string) => void
}

const MaintenanceModal: React.FC<IProps> = ({
  show,
  boothId,
  boothName,
  services,
  initialSelected,
  onClose,
  onDone
}) => {
  const [selected, setSelected] = useState<string[]>([])
  const [working, setWorking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Modal her açıldığında MEVCUT durumdan başla — bayat seçim gösterilmesin.
  useEffect(() => {
    if (!show) return
    setSelected(Array.isArray(initialSelected) ? [...initialSelected] : [])
    setError(null)
  }, [show, initialSelected])

  const allIds = useMemo(() => services.map((s) => s._id), [services])
  const allChecked = allIds.length > 0 && allIds.every((id) => selected.includes(id))
  const wasOn = (initialSelected?.length ?? 0) > 0

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const toggleAll = () => setSelected(allChecked ? [] : [...allIds])

  const handleSave = async () => {
    if (!boothId) return
    setWorking(true)
    setError(null)
    try {
      const api = new APIClient()
      const res: any = await api.put(`${BOOTH_RECORD_PATH}/${boothId}/maintenance`, {
        services: selected
      })
      if (res?.success) {
        const n = res.maintenanceCount ?? selected.length
        onDone(
          n === 0
            ? `${boothName}: bakım kaldırıldı, tüm hizmetler aktif.`
            : n === allIds.length
              ? `${boothName}: TÜM hizmetler bakıma alındı.`
              : `${boothName}: ${n} hizmet bakıma alındı.`
        )
        onClose()
      } else {
        setError(res?.message || 'Bakım durumu güncellenemedi.')
      }
    } catch (e: any) {
      setError(typeof e === 'string' ? e : 'Bakım durumu güncellenemedi.')
    } finally {
      setWorking(false)
    }
  }

  return (
    <Modal isOpen={show} toggle={onClose} centered>
      <ModalBody className='p-4'>
        <div className='text-center mb-3'>
          <i className='mdi mdi-wrench-outline display-5 text-warning'></i>
          <h5 className='mt-2 mb-1'>Bakıma Al</h5>
          <p className='text-muted mb-0'>{boothName}</p>
        </div>

        {services.length === 0 ? (
          <div className='alert alert-warning mb-0'>
            Bu kabine atanmış hizmet yok. Önce <strong>Details</strong> ekranından
            hizmet ekleyin.
          </div>
        ) : (
          <>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <span className='text-muted small'>Bakıma alınacak hizmetler</span>
              <button
                type='button'
                className='btn btn-sm btn-link p-0'
                onClick={toggleAll}
                disabled={working}
              >
                {allChecked ? 'Seçimi temizle' : 'Tümünü seç'}
              </button>
            </div>

            <div className='border rounded p-2 mb-3' style={{ maxHeight: 260, overflowY: 'auto' }}>
              {services.map((s) => (
                <label
                  key={s._id}
                  className='d-flex align-items-center gap-2 py-2 px-1 mb-0'
                  style={{ cursor: working ? 'default' : 'pointer' }}
                >
                  <input
                    type='checkbox'
                    className='form-check-input mt-0'
                    checked={selected.includes(s._id)}
                    onChange={() => toggle(s._id)}
                    disabled={working}
                  />
                  <span>{s.label || s.name || s._id}</span>
                </label>
              ))}
            </div>

            <p className='text-muted small mb-0'>
              Seçilen hizmetler kabinde <strong>solgun</strong> görünür, seçilemez ve
              dokunulduğunda <em>“şu an bakımdayız”</em> mesajı çıkar. Tümü seçilirse
              kabin tam ekran bakım ekranına geçer.
              {' '}Devam eden (ödemesi alınmış) işlem <strong>kesilmez</strong>.
              {' '}Hiçbiri seçili değilken kaydetmek bakımı <strong>kaldırır</strong>.
            </p>
          </>
        )}

        {error && <div className='alert alert-danger mt-3 mb-0'>{error}</div>}

        <div className='d-flex gap-2 mt-4'>
          <button
            type='button'
            className='btn btn-light flex-fill'
            onClick={onClose}
            disabled={working}
          >
            Vazgeç
          </button>
          <button
            type='button'
            className={`btn flex-fill ${selected.length === 0 ? 'btn-success' : 'btn-warning'}`}
            onClick={handleSave}
            disabled={working || services.length === 0}
          >
            {working && <Spinner size='sm' className='me-2' />}
            {selected.length === 0
              ? wasOn
                ? 'Bakımdan Çıkar'
                : 'Kaydet'
              : 'Bakıma Al'}
          </button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default MaintenanceModal
